/**
 * Helpers for BCP 47 language codes.
 *
 * Underlying data (analytics, persistence, API calls) always uses the
 * canonical `xx-YY` form. `formatCompactCode` is DISPLAY-ONLY (UI strings);
 * `normalizeLangKey` produces the canonical persistence/key form.
 */

import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

const logger = LogManager.getLogger('util:bcp47');

/**
 * Canonicalize a language code to a stable BCP 47 key for persistence
 * (`en-us` / `EN-US` → `en-US`, `pt-br` → `pt-BR`). Used to key per-language
 * karaoke so one language can't split across casings (the transcript emits
 * `en-us` while showcase records use `pt-BR`). Falls back to a lowercased trim
 * when the tag is malformed (`Intl.getCanonicalLocales` throws), so it is total
 * and never throws. (PLAT-10788)
 */
export function normalizeLangKey(code: string): string {
  const trimmed = code.trim();
  if (!trimmed) return trimmed;
  try {
    return Intl.getCanonicalLocales(trimmed)[0] ?? trimmed.toLowerCase();
  } catch (error) {
    logger.warn('normalizeLangKey: failed to canonicalize language code', {
      code: trimmed,
      error: extractErrorInfo(error),
    });
    return trimmed.toLowerCase();
  }
}

/**
 * Compact display form of a BCP 47 code. When the region subtag matches
 * the language subtag (e.g. "fr-FR", "it-IT", "de-DE"), collapse to just
 * the language subtag ("fr", "it", "de"). Otherwise return the input
 * unchanged — the region carries meaning ("en-US" vs "en-GB", "pt-BR"
 * vs "pt-PT"). Case-insensitive comparison so "en-en" and "EN-EN" both
 * collapse.
 *
 * Not for UI code labels any more (PLAT-11652): the transcript language
 * menu and the translation-memory settings dropdown show every code in
 * canonical form, because a bare "fr" beside "en-US" and "pt-BR" read as a
 * different kind of code. Kept as a comparison key (`PublishVoiceNotice`).
 *
 * Codes with more than two subtags (e.g. "zh-Hans-CN") are returned
 * unchanged — the helper only collapses the two-part form.
 */
export function formatCompactCode(code: string): string {
  const parts = code.split('-');
  if (parts.length !== 2) return code;
  const [lang, region] = parts;
  if (lang.toLowerCase() === region.toLowerCase()) return lang;
  return code;
}
